import { showToast } from "../../utils/toast";
import { sendJson } from "./request";
import { openTaskModal } from "./tasks_export";

const TICK_MS = 30_000;
const SENDING_MS = 48 * 3600 * 1000;
const LATE_MS = 96 * 3600 * 1000;
const PANES = ["campaigns", "users", "notes"];

const list = (): HTMLElement | null => document.querySelector<HTMLElement>("[data-cs-focus]");
const panel = (): HTMLElement | null => document.querySelector<HTMLElement>("[data-cs-focus-panel]");
const rows = (): HTMLElement[] => Array.from(document.querySelectorAll<HTMLElement>("[data-cs-focus] .fr"));
const drafts: Record<string, string> = {};
let openId: string | null = null;

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function sinceLabel(ms: number): string {
  const minutes = Math.floor(ms / 60000);
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  return days ? `${days}d ${pad(hours)}h ${pad(minutes % 60)}m` : `${hours}h ${pad(minutes % 60)}m`;
}

function tick(): void {
  if (document.hidden) return;
  document.querySelectorAll<HTMLElement>("[data-cs-focus] [data-since], [data-cv-grid] [data-since]").forEach((cell) => {
    const ms = Date.now() - Number(cell.dataset.since);
    const value = cell.querySelector<HTMLElement>("[data-since-value]");
    if (value) value.textContent = sinceLabel(ms);
    cell.classList.toggle("fs-good", ms < SENDING_MS);
    cell.classList.toggle("fs-fair", ms >= SENDING_MS && ms < LATE_MS);
    cell.classList.toggle("fs-poor", ms >= LATE_MS);
  });
}

function markSelected(): void {
  rows().forEach((row) => row.classList.toggle("sel", row.dataset.fid === openId));
  const index = rows().findIndex((row) => row.dataset.fid === openId);
  const pos = panel()?.querySelector<HTMLElement>("[data-cs-panel-pos]");
  if (pos) pos.textContent = index >= 0 ? `${index + 1} of ${rows().length}` : "Not in this view";
  panel()?.querySelectorAll<HTMLButtonElement>("[data-cs-panel-step]").forEach((button) => {
    const step = Number(button.dataset.csPanelStep);
    button.disabled = index < 0 || !rows()[index + step];
  });
}

function saveDraft(): void {
  const input = panel()?.querySelector<HTMLTextAreaElement>("#cs-note-input");
  if (openId && input) drafts[openId] = input.value;
}

const SKELETON = `
  <div class="cp-root cp-loading" aria-busy="true" aria-label="Loading client">
    <div class="cp-head">
      <span class="cp-skel cp-skel-logo"></span>
      <div class="cp-title"><span class="cp-skel cp-skel-line" style="width: 180px"></span><span class="cp-skel cp-skel-line cp-skel-sm" style="width: 240px"></span></div>
    </div>
    <div class="cp-sum"><span class="cp-skel cp-skel-pill"></span><span class="cp-skel cp-skel-pill"></span><span class="cp-skel cp-skel-line" style="width: 120px; margin-left: auto"></span></div>
    <div class="cp-body">
      <div class="fr-camps"><span class="cp-skel cp-skel-line" style="width: 90px"></span><span class="cp-skel cp-skel-block"></span></div>
      <div class="cp-grid">
        <div class="fr-col cp-tasks"><span class="cp-skel cp-skel-line" style="width: 120px"></span><span class="cp-skel cp-skel-block cp-skel-short"></span></div>
        <div class="fr-col"><span class="cp-skel cp-skel-line" style="width: 70px"></span><span class="cp-skel cp-skel-block cp-skel-short"></span></div>
        <div class="fr-col"><span class="cp-skel cp-skel-line" style="width: 70px"></span><span class="cp-skel cp-skel-block cp-skel-short"></span></div>
      </div>
    </div>
  </div>`;

function placeBelowMobileHeader(box: HTMLElement): void {
  const header = document.querySelector<HTMLElement>(".mobile-header");
  const visible = header && header.getClientRects().length > 0 && getComputedStyle(header).position === "fixed";
  box.style.setProperty("--cp-top", visible ? `${header!.offsetHeight}px` : "0px");
}

async function openPanel(row: HTMLElement): Promise<void> {
  const box = panel();
  if (!box) return;
  saveDraft();
  openId = row.dataset.fid || null;
  box.innerHTML = SKELETON;
  placeBelowMobileHeader(box);
  box.classList.add("open");
  document.body.classList.add("cp-open");
  markSelected();
  try {
    const response = await fetch(row.dataset.focusUrl!, { headers: { Accept: "text/html" } });
    if (!response.ok) throw new Error(String(response.status));
    if (openId !== row.dataset.fid) return;
    box.innerHTML = await response.text();
  } catch (_e) {
    box.innerHTML = '<div class="cp-error">Couldn\'t load this client. <button type="button" class="cp-retry" data-cs-panel-retry>Retry</button></div>';
    return;
  }
  const pane = PANES.includes(window.location.hash.slice(1)) ? window.location.hash.slice(1) : "campaigns";
  history.replaceState(history.state, "", `${window.location.pathname}${window.location.search}#${pane}`);
  window.dispatchEvent(new HashChangeEvent("hashchange"));
  markSelected();
  (window as any).mountLogos?.();
}

function closePanel(): void {
  saveDraft();
  openId = null;
  panel()?.classList.remove("open");
  document.body.classList.remove("cp-open");
  if (panel()) panel()!.innerHTML = "";
  history.replaceState(history.state, "", `${window.location.pathname}${window.location.search}`);
  window.dispatchEvent(new HashChangeEvent("hashchange"));
  markSelected();
}

function step(by: number): void {
  const all = rows();
  const index = all.findIndex((row) => row.dataset.fid === openId);
  const next = all[index + by];
  if (index < 0 || !next) return showToast(by > 0 ? "End of the list" : "Start of the list");
  next.scrollIntoView({ block: "nearest", behavior: "smooth" });
  void openPanel(next);
}

function reopen(): void {
  const row = rows().find((r) => r.dataset.fid === openId);
  if (row) void openPanel(row);
}

function focusNote(): void {
  const link = panel()?.querySelector<HTMLElement>('[data-cs-pane-link="notes"]');
  link?.click();
  const wait = (tries: number): void => {
    const input = panel()?.querySelector<HTMLTextAreaElement>("#cs-note-input");
    if (input) return input.focus();
    if (tries > 0) window.setTimeout(() => wait(tries - 1), 150);
  };
  wait(20);
}

async function tickTask(item: HTMLElement): Promise<void> {
  const done = item.dataset.status === "COMPLETED";
  const body = new FormData();
  body.set("id", item.dataset.csNaTask || "");
  body.set("status", done ? "NOT_STARTED" : "COMPLETED");
  const { ok } = await sendJson("/customer_success/change_status_stask", "PATCH", body, false);
  if (!ok) return showToast("Couldn't update the task");
  item.dataset.status = done ? "NOT_STARTED" : "COMPLETED";
  item.classList.toggle("done", !done);
  showToast(done ? "Task reopened" : "Task completed");
}

const overlayOpen = (): boolean =>
  Boolean(document.querySelector("[data-cs-drawer]:not([hidden]), [data-cs-user-drawer]:not([hidden]), [data-cs-task-modal]:not([hidden]), [data-cs-popover]:not([hidden])"));

function handleClick(event: MouseEvent): void {
  const target = event.target as HTMLElement;
  if (!list()) return;

  const open = target.closest<HTMLElement>("[data-cs-focus-open]");
  const row = open?.closest<HTMLElement>(".fr");
  if (row && !target.closest("a")) {
    event.preventDefault();
    return row.dataset.fid === openId ? closePanel() : void openPanel(row);
  }

  const box = panel();
  if (!box || !openId) return;
  if (box.contains(target)) {
    if (target.closest("[data-cs-panel-close]")) return closePanel();
    const stepButton = target.closest<HTMLElement>("[data-cs-panel-step]");
    if (stepButton) return step(Number(stepButton.dataset.csPanelStep));
    if (target.closest("[data-cs-panel-retry]")) return reopen();
    if (target.closest("[data-cs-panel-note]")) return focusNote();
    if (target.closest("[data-cs-panel-add-task]")) return openTaskModal({ customer_id: openId });
    const taskTick = target.closest<HTMLElement>("[data-cs-na-tick]");
    if (taskTick) return void tickTask(taskTick.closest<HTMLElement>("[data-cs-na-task]")!);
    return;
  }
  if (target.closest("[data-cs-drawer], [data-cs-user-drawer], [data-cs-task-modal], [data-cs-popover], .cs-drawer-backdrop, .toast")) return;
  if (document.body.contains(target)) closePanel();
}

function handleKey(event: KeyboardEvent): void {
  if (!openId || overlayOpen() || event.metaKey || event.ctrlKey || event.altKey) return;
  const field = event.target as HTMLElement;
  if (field.closest("input, textarea, select, [contenteditable]")) return;
  if (event.key === "Escape") return closePanel();
  if (event.key === "j" || event.key === "J" || event.key === "ArrowDown") {
    event.preventDefault();
    return step(1);
  }
  if (event.key === "k" || event.key === "K" || event.key === "ArrowUp") {
    event.preventDefault();
    return step(-1);
  }
  if (event.key === "n" || event.key === "N") {
    event.preventDefault();
    focusNote();
  }
}

function restoreDraft(): void {
  const input = panel()?.querySelector<HTMLTextAreaElement>("#cs-note-input:not([data-draft-restored])");
  if (!input || !openId) return;
  input.dataset.draftRestored = "1";
  if (drafts[openId] && !input.value) input.value = drafts[openId];
}

let bound = false;

export function bindCsFocus(): void {
  if (bound) return;
  bound = true;

  window.setInterval(tick, TICK_MS);
  document.addEventListener("visibilitychange", tick);
  window.addEventListener("resize", () => {
    const box = panel();
    if (box && openId) placeBelowMobileHeader(box);
  });
  document.addEventListener("click", handleClick);
  document.addEventListener("keydown", handleKey);
  document.addEventListener("input", (event) => {
    if ((event.target as HTMLElement).id === "cs-note-input") saveDraft();
  });
  new MutationObserver(() => openId && restoreDraft()).observe(document.body, { childList: true, subtree: true });
  document.addEventListener("cs-list:refreshed", () => {
    if (!openId) return;
    if (rows().some((row) => row.dataset.fid === openId)) reopen();
    else closePanel();
  });
  document.addEventListener("cs:task-saved", () => openId && reopen());
  document.addEventListener("turbo:before-cache", () => openId && closePanel());
}
